import { useEffect, useState } from "react";
import api from "@/lib/api";
import { formatApiError } from "@/lib/errors";
import { Mail, Send, Eye, Users } from "lucide-react";

export default function EmailPanel() {
  const [preview, setPreview] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [subs, setSubs] = useState(null);

  useEffect(() => {
    api.get("/admin/daily-preview").then((r) => setPreview(r.data)).catch(() => {});
    api.get("/stats/global").then((r) => setSubs(r.data.users)).catch(() => {});
  }, []);

  const doTest = async () => {
    setBusy(true); setMsg(null);
    try {
      const { data } = await api.post("/admin/send-test-email", testTo ? { to: testTo } : {});
      setMsg({ ok: true, text: `Test sent to ${data.sent_to} (id: ${data.id})` });
    } catch (e) {
      setMsg({ ok: false, text: formatApiError(e) });
    } finally { setBusy(false); }
  };

  const doSendAll = async () => {
    if (!window.confirm("Send today's email to all subscribed users?")) return;
    setBusy(true); setMsg(null);
    try {
      const { data } = await api.post("/admin/send-daily-email");
      setMsg({ ok: data.failed === 0, text: `Sent ${data.sent}, failed ${data.failed}.` });
    } catch (e) {
      setMsg({ ok: false, text: formatApiError(e) });
    } finally { setBusy(false); }
  };

  return (
    <div className="card p-6 mb-8" style={{ borderColor: "rgba(37,99,235,0.3)" }} data-testid="admin-email-panel">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
        <div className="flex items-start gap-3">
          <Mail size={22} style={{ color: "var(--ai)" }} className="mt-1" />
          <div>
            <div className="label-mono" style={{ color: "var(--ai)" }}>Daily email</div>
            <div className="font-display font-bold text-xl mt-1">
              {preview?.subject || "Loading…"}
            </div>
            <div className="text-sm mt-1" style={{ color: "var(--text-dim)" }}>
              <Users size={11} className="inline mr-1" /> Up to {subs ?? "—"} subscribed users
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button type="button" className="btn btn-secondary" onClick={() => setShowPreview(!showPreview)} data-testid="email-preview-button">
            <Eye size={14} /> {showPreview ? "Hide preview" : "Preview"}
          </button>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-3 items-start">
        <div className="flex items-center gap-2 flex-wrap">
          <input
            className="input flex-1 min-w-[200px]"
            placeholder="Test recipient (defaults to your admin email)"
            value={testTo}
            onChange={(e) => setTestTo(e.target.value)}
            data-testid="email-test-to-input"
          />
          <button type="button" className="btn btn-secondary" onClick={doTest} disabled={busy} data-testid="email-send-test-button">
            <Send size={14} /> Send test
          </button>
        </div>
        <div className="flex items-center justify-end">
          <button type="button" className="btn btn-ai" onClick={doSendAll} disabled={busy} data-testid="email-send-all-button">
            <Send size={14} /> Send to all subscribers
          </button>
        </div>
      </div>

      {msg && (
        <div className={`tag mt-4 inline-block ${msg.ok ? "tag-correct" : "tag-wrong"}`} data-testid="email-result">
          {msg.text}
        </div>
      )}

      {showPreview && preview?.html && (
        <div className="mt-6 border" style={{ borderColor: "var(--border)" }}>
          <iframe title="email preview" srcDoc={preview.html} className="w-full" style={{ height: "640px", background: "#050505", border: 0 }} data-testid="email-preview-iframe" />
        </div>
      )}
    </div>
  );
}
